import type { ChakraProviderProps, SystemContext, ThemeProps } from '@chakra-ui/react';
import { defaultConfig, createSystem, ChakraProvider, Theme } from '@chakra-ui/react';
import { useImperativeHandle, useMemo, type Ref } from 'react';
import { Toaster } from '../../components/Toaster';
import { config as defaultConfig_ } from '../configs';
import type { ThemeConfig, ThemeProviderValue } from './type';
import { themeContext, useThemeContextValue } from './context';

export interface ThemeProviderRef {
  value: ThemeProviderValue;
  chakraValue: SystemContext;
}

export interface ThemeProviderProps extends Omit<ChakraProviderProps, 'value'> {
  ref?: Ref<ThemeProviderRef>;
  themeConfigs?: ThemeConfig[];
  themeProps?: ThemeProps;
}

export function ThemeProvider({
  children,
  themeConfigs,
  themeProps,
  ref,
  ...props
}: ThemeProviderProps) {
  const value = useThemeContextValue();

  const chakraValue = useMemo(() => {
    return createSystem(defaultConfig, defaultConfig_, ...(themeConfigs || []));
  }, [themeConfigs]);

  useImperativeHandle(
    ref,
    () => ({
      value,
      chakraValue,
    }),
    [value, chakraValue]
  );

  return (
    <themeContext.Provider value={value}>
      <ChakraProvider {...props} value={chakraValue}>
        <Theme
          ref={value.themeRootRef}
          colorPalette={value.accent}
          pos="relative"
          {...themeProps}
        >
          {children}
          <Toaster></Toaster>
        </Theme>
      </ChakraProvider>
    </themeContext.Provider>
  );
}
